import { Bell, ChevronRight, Circle, CircleCheckBig, CircleDot, X } from "lucide-react";
import TaskResultsSummary, { hasTaskResults } from "@/components/tasks/results/TaskResultsSummary/TaskResultsSummary";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDurationShort, formatTimeDisplay } from "@/lib/time";
import { deriveTaskLifecycle, getTaskLifecycleLabel } from "@shared/taskLifecycle";

type TaskCardTask = {
  id: number;
  title: string;
  details?: string | null;
  notes?: string | null;
  log?: string | null;
  status: "pending" | "done" | "skipped";
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled: boolean;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  barColor?: string | null;
  bgColor?: string | null;
  listName?: string | null;
  listColor?: string | null;
  listBgColor?: string | null;
};

const metaIconClassName = "ui-icon h-3.5 w-3.5";
const metaIconStrokeWidth = 2.5;

export default function TaskCard({
  task: t,
  selected = false,
  dimCompleted = true,
  onClick,
}: {
  task: TaskCardTask;
  selected?: boolean;
  dimCompleted?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const resolved = resolveTaskColors({
    barColor: t.barColor,
    bgColor: t.bgColor,
    listColor: t.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: t.listBgColor ?? null,
  });
  const lifecycle = deriveTaskLifecycle(t);
  const isDoing = lifecycle === "doing";
  const isDone = lifecycle === "done";
  const isSkipped = lifecycle === "skipped";
  const isPending = lifecycle === "pending";
  const dim = dimCompleted && (isDone || isSkipped);
  const showResults = hasTaskResults({
    status: t.status,
    startedAt: t.startedAt as Date | null,
    completedAt: t.completedAt as Date | null,
    notes: t.notes ?? null,
    log: t.log ?? null,
  });

  return (
    <div className={`ui-task-card group relative border rounded-xl overflow-hidden transition-all ${
        selected
          ? "border-foreground/60 ring-1 ring-foreground/20"
          : "border-border/60 hover:border-border"
      } ${dim ? "opacity-55" : ""}`}
      style={{ background: resolved.soft }}
    >
      <div className="flex items-stretch">
        <span className="w-1.5" style={{ background: resolved.bar }} aria-hidden />
        <div className="ui-task-card flex-1 flex items-start gap-3 px-4 py-3 min-w-0">
          <button
            type="button"
            className="ui-task-button flex flex-1 items-stretch gap-3 min-w-0 text-left"
            onClick={onClick}
            style={{ color: resolved.ink }}
          >
            <div className="ui-content-wrapper flex-1 min-w-0 space-y-0">
              <div className="ui-top-row flex items-start justify-between gap-3">
                <div
                  className={`ui-title inline-flex items-center gap-1.5 font-bold text-base leading-snug min-w-0 ${
                    isSkipped ? "line-through" : ""
                  }`}
                >
                  {isDone && (
                    <CircleCheckBig
                      className={metaIconClassName}
                      strokeWidth={metaIconStrokeWidth}
                      aria-label="Done"
                    />
                  )}
                  {isDoing && (
                    <CircleDot
                      className={metaIconClassName}
                      strokeWidth={metaIconStrokeWidth}
                      aria-label={getTaskLifecycleLabel(lifecycle)}
                    />
                  )}
                  {isSkipped && (
                    <X
                      className={metaIconClassName}
                      strokeWidth={metaIconStrokeWidth}
                      aria-label="Skipped"
                    />
                  )}
                  {isPending && (
                    <Circle
                      className={metaIconClassName}
                      strokeWidth={metaIconStrokeWidth}
                      aria-label="Pending"
                    />
                  )}
                  <span className="ui-title-text min-w-0 truncate">{t.title}</span>
                </div>
                
                {t.listName && (
                  <span
                    className="ui-area-name inline-flex max-w-32 shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.14em] truncate"
                    style={{
                      background: `color-mix(in oklch, ${resolved.bar} 18%, transparent)`,
                      color: resolved.ink,
                    }}
                  >
                    {t.listName}
                  </span>
                )}
              </div>

              <div className="ui-meta-row flex w-full min-w-0 flex-row items-center">
                <div className="ui-meta-wrapper min-w-0 flex-1 gap-4">
                  {t.details && (
                    <div
                      className="ui-details text-[15px] truncate font-semibold"
                      style={{ opacity: 0.75 }}
                    >
                      {t.details}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] tabular-nums font-mono"
                    style={{ color: resolved.ink, opacity: 0.7 }}
                  >
                    {t.startMinutes !== null && t.startMinutes !== undefined && (
                      <span className="inline-flex items-center gap-1 text-sm bold" title="Planned start time">
                        {formatTimeDisplay(t.startMinutes)}
                      </span>
                    )}
                    {t.alertEnabled && (
                      <span className="inline-flex items-center gap-1" title="Alert on">
                        <Bell className={metaIconClassName} strokeWidth={metaIconStrokeWidth} />
                      </span>
                    )}
                    {t.durationMin && (
                      <span className="inline-flex items-center gap-1" title="Duration">
                        {formatDurationShort(t.durationMin)}
                      </span>
                    )}
                  </div>

                  {showResults && (
                    <TaskResultsSummary
                      status={t.status}
                      startedAt={t.startedAt as Date | null}
                      completedAt={t.completedAt as Date | null}
                      notes={t.notes ?? null}
                      log={t.log ?? null}
                      color={resolved.ink}
                    />
                  )}
                </div>

                <ChevronRight className="ui-chevron ml-auto h-5.5 w-5.5 shrink-0 opacity-85" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
